import { get, head, map } from 'lodash/fp';
import { createAction } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { getScopeEnterpriseId } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getResource } from '@mews-commander/legacy-entities/src/ducks/resources';
import { getEnterpriseIdFromServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { getResourceCategoryIdsByResourceCategoryTypeName } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { navigateInBaseModal } from '../../app/actions/common';
import { getHomeUrl } from '../../../router/selectors';
import { changeEnterprise } from '../../app/actions/profile';
import NewReservationScreenStep from '../enums/NewReservationScreenStep';
import {
    getAvailabilityAdjustmentsUniqResourceCategoryIdsByResourceCategoryType,
    getResourceCategoryIdsOfSelectedResourceCategoryType,
    getSelectedServiceUserProfileId,
} from '../selectors/entities';
import {
    getReservationGroupUrl,
    getServiceId,
    getResourceCategoryTypeCategoryPricing,
    hasAnyBookedServiceSendConfirmationEmailByDefault,
    getSelectedResourceCategoryType,
    getSelectedAvailabilityBlockId,
    getFloorNumbersByResourceCategoryIds,
    getAvailablityBlockResourceCategoryIdsByType,
    getResourceCategoryTypes,
} from '../selectors';
import MailReceivers from '../enums/MailReceivers';
import { FILTER_DEFAULT_VALUES } from '../reducers/screens';
import { changeConfirmationEmailReceiversValue } from './options';
import {
    STEP_CHANGED,
    RESOURCE_CATEGORY_TYPE_SELECTED,
    RESET_SCREEN,
    FILTERS_CHANGED,
    SHOW_CHANGE_DATE_DIALOG,
    HIDE_CHANGE_DATE_DIALOG,
    SHOW_CHANGE_SERVICE_DIALOG,
    HIDE_CHANGE_SERVICE_DIALOG,
    SHOW_NRS_LOADER,
    HIDE_NRS_LOADER,
} from './types';
import { fetchRatePricing } from './api';

export const showDates = (scope: string) => createAction(STEP_CHANGED, NewReservationScreenStep.Dates, { scope });
export const showRates = (scope: string) => createAction(STEP_CHANGED, NewReservationScreenStep.Rates, { scope });
export const showSummary =
    (scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const hasAnyBookedServiceSendConfirmationEmailEnabled = hasAnyBookedServiceSendConfirmationEmailByDefault(
            getState(),
            { scope }
        );
        const confirmationType = hasAnyBookedServiceSendConfirmationEmailEnabled
            ? MailReceivers.CustomerAndBooker
            : MailReceivers.None;
        dispatch(changeConfirmationEmailReceiversValue(confirmationType.value, scope));
        dispatch(createAction(STEP_CHANGED, NewReservationScreenStep.Summary, { scope }));
    };

const STEPS = {
    [NewReservationScreenStep.Dates]: showDates,
    [NewReservationScreenStep.Rates]: showRates,
    [NewReservationScreenStep.Summary]: showSummary,
};

export const changeStep = (step: NewReservationScreenStep, scope: string) => STEPS[step](scope);

export const changeFilters =
    ({ filterValues, scope }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        let values = filterValues;
        const state = getState();
        const serviceId = getServiceId(state, { scope });
        const availabilityBlockId = getSelectedAvailabilityBlockId(state, { scope });

        if (filterValues === undefined || filterValues.selectedResourceId === null) {
            const resourceCategoryIds = availabilityBlockId
                ? getAvailabilityAdjustmentsUniqResourceCategoryIdsByResourceCategoryType(state, {
                      scope,
                      serviceId,
                      availabilityBlockId,
                  })
                : getResourceCategoryIdsOfSelectedResourceCategoryType(state, { scope, serviceId });
            const floorNumbers = getFloorNumbersByResourceCategoryIds(state, {
                serviceId,
                resourceCategoryIds,
            });
            values = { ...FILTER_DEFAULT_VALUES, selectedFloorNumbers: floorNumbers };
        } else if (filterValues.selectedResourceId) {
            const { selectedResourceId } = filterValues;
            const enterpriseId = getEnterpriseIdFromServiceId(state, { serviceId });
            const resource = getResource(state, { id: selectedResourceId, enterpriseId });
            const floorNumber = (resource as $TSFixMe)?.data?.value?.floorNumber;
            if (floorNumber !== undefined) {
                values = { selectedResourceId, selectedFloorNumbers: [floorNumber] };
            }
        }

        dispatch(createAction(FILTERS_CHANGED, values, { scope }));
    };

export const selectResourceCategoryType =
    (resourceCategoryTypeName: string, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const serviceId = getServiceId(state, { scope });
        const availabilityBlockId = getSelectedAvailabilityBlockId(state, { scope });
        const resourceCategoryIds = availabilityBlockId
            ? getAvailablityBlockResourceCategoryIdsByType(state, {
                  serviceId,
                  resourceCategoryTypeName,
                  availabilityBlockId,
              })
            : getResourceCategoryIdsByResourceCategoryTypeName(state, {
                  serviceId,
                  resourceCategoryTypeName,
              });

        const pricing = map((id) =>
            getResourceCategoryTypeCategoryPricing(state, { resourceCategoryTypeCategoryId: id, scope })
        )(resourceCategoryIds);
        dispatch(
            createAction(
                RESOURCE_CATEGORY_TYPE_SELECTED,
                { resourceCategoryType: resourceCategoryTypeName, pricing },
                { scope }
            )
        );

        if (serviceId !== undefined) {
            dispatch(changeFilters({ scope }));
            dispatch(fetchRatePricing({ scope, resourceCategoryIds, origin: 'NRS-selectResourceCategoryType' }));
        }
    };

export const preselectResourceCategoryType =
    ({ serviceIds, scope }: { serviceIds: string[]; scope: string }): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const resourceCategoryTypes = getResourceCategoryTypes(state, { serviceIds, scope });
        const selectedCategoryType = getSelectedResourceCategoryType(state, { scope });
        const preselectedResourceCategoryType =
            resourceCategoryTypes.find(({ value }) => selectedCategoryType && value === selectedCategoryType.value) ||
            head(resourceCategoryTypes);
        dispatch(
            createAction(
                RESOURCE_CATEGORY_TYPE_SELECTED,
                { resourceCategoryType: get('name', preselectedResourceCategoryType) },
                { scope }
            )
        );
        dispatch(changeFilters({ scope }));
    };

export const switchAccountToReservationGroup =
    (scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const props = { scope };

        const url = getReservationGroupUrl(state, props);
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const profileId = getSelectedServiceUserProfileId(state, props);
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const enterpriseId = getScopeEnterpriseId(state, { profileId });
        dispatch(changeEnterprise({ profileId, enterpriseId }, url));
    };

export const closeScreen =
    (scope: string): CommanderThunkAction =>
    (dispatch, getState, { resolveRoute }) => {
        const url = getHomeUrl(getState(), { resolveRoute });

        dispatch(createAction(RESET_SCREEN, undefined, { scope }));
        dispatch(navigateInBaseModal(url));
    };

export const showChangeDateDialog = (scope: string) => createAction(SHOW_CHANGE_DATE_DIALOG, null, { scope });
export const hideChangeDateDialog = (scope: string) => createAction(HIDE_CHANGE_DATE_DIALOG, null, { scope });
export const showChangeServiceDialog = (scope: string) => createAction(SHOW_CHANGE_SERVICE_DIALOG, null, { scope });
export const hideChangeServiceDialog = (scope: string) => createAction(HIDE_CHANGE_SERVICE_DIALOG, null, { scope });

export const showNRSLoader = () => createAction(SHOW_NRS_LOADER);
export const hideNRSLoader = () => createAction(HIDE_NRS_LOADER);
